import { useState, useEffect } from "react";
import { SuperAdminLayout } from "@/components/admin/SuperAdminLayout";
import { ClientReferralKanbanBoard } from "@/components/referrals/ClientReferralKanbanBoard";
import { ClientReferralStatusCard } from "@/components/admin/ClientReferralStatusCard";
import { ClientReferralViewer } from "@/components/referrals/ClientReferralViewer";
import { Card, CardContent } from "@/components/ui/card";
import { FilterSection, type FilterConfig } from "@/components/ui/FilterSection";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { 
  getAdminClientReferrals, 
  getAdminClientReferralStatusCounts,
  subscribeToAdminClientReferrals,
  subscribeToAdminClientReferralStatusCounts,
  updateClientReferralStatus,
  addClientReferralNote,
  migrateClientReferralTimestamps,
  ClientReferralWithUI
} from "@/api/clientReferrals";
import { getClientReferralConfig } from "@/lib/kanbanConfigs.tsx";

// Client referral status type
type ClientReferralStatus = 'pending' | 'contacted' | 'meeting_scheduled' | 'converted' | 'rejected';

export default function ClientReview() {
  const { toast } = useToast();
  const { userData } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedReferral, setSelectedReferral] = useState<ClientReferralWithUI | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncingReferrals, setSyncingReferrals] = useState<Set<string>>(new Set());

  // Real data from API
  const [clientReferrals, setClientReferrals] = useState<ClientReferralWithUI[]>([]);
  const [statusCounts, setStatusCounts] = useState({
    pending: 0,
    contacted: 0,
    meeting_scheduled: 0,
    converted: 0,
    rejected: 0
  });

  // Update selectedReferral when clientReferrals data changes
  useEffect(() => {
    if (selectedReferral && clientReferrals.length > 0) {
      const updatedReferral = clientReferrals.find(r => r.id === selectedReferral.id);
      if (updatedReferral) {
        setSelectedReferral(updatedReferral);
      }
    }
  }, [clientReferrals, selectedReferral?.id]);

  // Load initial data and set up real-time subscriptions
  useEffect(() => {
    let unsubscribeReferrals: (() => void) | undefined;
    let unsubscribeCounts: (() => void) | undefined;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Run migration for missing updatedAt fields (runs only once, safe to call multiple times)
        try {
          await migrateClientReferralTimestamps();
        } catch (migrationError) {
          console.warn('Migration warning (non-critical):', migrationError);
        }

        // Load initial data
        const [initialReferrals, initialCounts] = await Promise.all([
          getAdminClientReferrals(),
          getAdminClientReferralStatusCounts()
        ]);

        setClientReferrals(initialReferrals);
        setStatusCounts(initialCounts);

        // Set up real-time subscriptions
        unsubscribeReferrals = subscribeToAdminClientReferrals((newReferrals) => {
          setClientReferrals(newReferrals);
        });

        unsubscribeCounts = subscribeToAdminClientReferralStatusCounts((newCounts) => {
          setStatusCounts(newCounts);
        });

      } catch (err) {
        console.error('Error loading client referral data:', err);
        setError('Failed to load client referral data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Cleanup subscriptions on unmount
    return () => {
      if (unsubscribeReferrals) unsubscribeReferrals();
      if (unsubscribeCounts) unsubscribeCounts();
    };
  }, []);

  const handleStatusChange = async (referralId: string, newStatus: ClientReferralStatus) => {
    if (!userData) {
      setError('You must be logged in to update client referral status.');
      return;
    }

    const referral = clientReferrals.find(r => r.id === referralId);
    const companyName = referral?.companyName || 'client referral';
    
    // Only proceed if status is actually changing
    if (referral && referral.status === newStatus) {
      return;
    }
    
    // Immediate optimistic update for responsive UI
    setClientReferrals(prev => prev.map(ref => 
      ref.id === referralId 
        ? { ...ref, status: newStatus, updatedAt: new Date() }
        : ref
    ));
    
    // Add to syncing set for visual feedback
    setSyncingReferrals(prev => new Set(prev).add(referralId));
    
    try {
      // Make the actual API call to update Firestore
      await updateClientReferralStatus(referralId, newStatus, userData.id);
      
      // Remove from syncing set
      setSyncingReferrals(prev => {
        const newSet = new Set(prev);
        newSet.delete(referralId);
        return newSet;
      });
      
      toast({
        title: "Status Updated",
        description: `${companyName} status changed to ${newStatus.replace('_', ' ')}.`
      });
    } catch (error) {
      // Remove from syncing set
      setSyncingReferrals(prev => {
        const newSet = new Set(prev);
        newSet.delete(referralId);
        return newSet;
      });
      
      // Revert optimistic update on failure
      setClientReferrals(prev => prev.map(ref => 
        ref.id === referralId 
          ? { ...ref, status: referral?.status || ref.status }
          : ref
      ));
      
      // Show error toast
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Update Failed",
        description: `Failed to update ${companyName} status. Please try again.`,
        variant: "destructive"
      });
      
      setError(`Failed to update ${companyName} status. The change has been reverted.`);
    }
  };

  const handleViewReferral = (referral: ClientReferralWithUI) => {
    setSelectedReferral(referral);
    setIsViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setIsViewerOpen(false);
    setSelectedReferral(null);
  };

  const handleAddNote = async (referralId: string, note: string) => {
    if (!userData) {
      setError('You must be logged in to add notes.');
      return;
    }

    const referral = clientReferrals.find(r => r.id === referralId);
    const companyName = referral?.companyName || 'client referral';
    
    try {
      await addClientReferralNote(referralId, note, userData.id);
      
      toast({
        title: "Note Added",
        description: `Note added to ${companyName}.`
      });
    } catch (error) {
      console.error('Error adding note:', error);
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Failed to Add Note",
        description: `Failed to add note to ${companyName}. Please try again.`,
        variant: "destructive"
      });
      
      setError(`Failed to add note to ${companyName}.`);
    }
  };

  // Create custom kanban columns excluding converted and rejected
  const activeKanbanColumns = getClientReferralConfig().filter(
    column => column.id !== 'converted' && column.id !== 'rejected'
  );

  const filteredReferrals = clientReferrals.filter(referral => {
    const matchesSearch = 
      referral.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      referral.pointOfContact.toLowerCase().includes(searchTerm.toLowerCase()) ||
      referral.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      referral.referrerName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || referral.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Filter configurations
  const statusFilterConfig: FilterConfig = {
    placeholder: "Status",
    options: [
      { value: "all", label: "All Status" },
      { value: "pending", label: "Pending" },
      { value: "contacted", label: "Contacted" },
      { value: "meeting_scheduled", label: "Meeting Scheduled" },
      { value: "converted", label: "Converted" },
      { value: "rejected", label: "Rejected" }
    ]
  };

  // Check if any filters are active
  const hasActiveFilters = statusFilter !== "all";

  const clearFilters = () => {
    setStatusFilter("all");
  };

  // Show loading state
  if (loading) {
    return (
      <SuperAdminLayout
        title="Client Review"
        subtitle="Manage client referral pipeline and track conversion progress."
      >
        <LoadingScreen />
      </SuperAdminLayout>
    );
  }

  // Show error state
  if (error) {
    return (
      <SuperAdminLayout
        title="Client Review"
        subtitle="Manage client referral pipeline and track conversion progress."
      >
        <Card className="p-6 text-center">
          <CardContent>
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </CardContent>
        </Card>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout
      title="Client Review"
      subtitle="Manage client referral pipeline and track conversion progress."
    >
      {/* Pipeline Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-8 lg:mb-10">
        <ClientReferralStatusCard
          status="pending"
          title="PENDING"
          value={statusCounts.pending}
          testId="card-pending"
        />

        <ClientReferralStatusCard
          status="contacted"
          title="CONTACTED"
          value={statusCounts.contacted}
          testId="card-contacted"
        />

        <ClientReferralStatusCard
          status="meeting_scheduled"
          title="MEETING SCHEDULED"
          value={statusCounts.meeting_scheduled}
          testId="card-meeting-scheduled"
        />

        <ClientReferralStatusCard
          status="converted"
          title="CONVERTED"
          variant="dark"
          value={statusCounts.converted}
          testId="card-converted"
        />
      </div>

      {/* Filters and Search */}
      <div className="mb-8 lg:mb-10">
        <FilterSection
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search companies, contacts, referrers..."
          filters={{
            status: {
              value: statusFilter,
              onChange: setStatusFilter,
              config: statusFilterConfig
            }
          }}
          useCard={true}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={clearFilters}
        />
      </div>

      {/* Kanban Board - Responsive Container */}
      <div className="w-full">
        <div className="min-h-[600px] lg:min-h-[700px] xl:min-h-[800px] overflow-x-auto">
          <div className="min-w-[600px] lg:min-w-full">
            <ClientReferralKanbanBoard 
              referrals={filteredReferrals}
              onStatusChange={handleStatusChange}
              onViewReferral={handleViewReferral}
              syncingReferrals={syncingReferrals}
              customColumns={activeKanbanColumns}
            />
          </div>
        </div>
      </div>

      {/* Client Referral Viewer Modal */}
      <ClientReferralViewer
        key={selectedReferral?.id || 'no-referral'}
        referral={selectedReferral}
        isOpen={isViewerOpen}
        onClose={handleCloseViewer}
        onStatusChange={handleStatusChange}
        onAddNote={handleAddNote}
      />
    </SuperAdminLayout>
  );
}